// The waiting lobby's two panels (docs/plans/2026-10-18-waiting-lobby.md): the doomscroll before you join, and the
// room widget while it fills. Copy lives in data/feed.json; every person in it is invented.
import FEED from '../../data/feed.json';

/** A post as data/feed.json holds it. `sentiment` and `player_actionable` are for the feed's next layout. */
export interface Post {
  username: string; content: string; sentiment: string; id: string; time_posted: string;
  replies: number; retweets: number; likes: number; player_actionable: boolean;
}
const POSTS = FEED.posts as Post[];
export const PITCH = FEED.pitch, LEAVE = FEED.leave;
/** posts the docked feed opens with, how many it pulls in near its end, how many it keeps, its drift (px/s), and the
 * reduced-motion step (s) */
const SHOW = 7, MORE = 5, KEEP = 24, DRIFT_PX_S = 32, STEP_S = 6;
/** the tag over a post, and the card's style, by sentiment; the rest go untagged */
export const TAG: Record<string, { label: string; kind: string }> = { urgent_update: { label: '⚠ Update', kind: 'alert' } };

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };
function shuffle<T>(a: T[], rnd = Math.random): T[] { const b = a.slice(); for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; }

/** Minutes behind now, from "4m", "3h" or "2d"; anything else sorts last. */
export function minutesAgo(t: string): number {
  const m = /^(\d+)\s*([mhd])$/.exec(t.trim());
  return m ? Number(m[1]) * { m: 1, h: 60, d: 1440 }[m[2] as 'm' | 'h' | 'd'] : Infinity;
}
/** 411, 3.5k, 19k: the way a feed prints a count. */
export function count(n: number): string {
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k < 10 ? k.toFixed(1).replace(/\.0$/, '') : Math.round(k)}k`;
}
/** The display name: the handle without its @, underscores as spaces. */
export const postName = (p: Post) => p.username.replace(/^@/, '').replace(/_/g, ' ');
/** n posts drawn at random from the whole set, newest first, so each visit reads like a fresh timeline. */
export function pickPosts(n: number, rnd = Math.random): Post[] {
  return shuffle(POSTS, rnd).slice(0, n).sort((a, b) => minutesAgo(a.time_posted) - minutesAgo(b.time_posted));
}

/**
 * The doomscroll, docked beside the game while your room fills. It used to stand between Join and the lobby; now Join
 * connects at once and the feed fills the wait instead of adding to it. A phone on the left that scrolls itself and
 * cannot be scrolled: it is something to glance at while you walk, never something to work. It runs on without end
 * (posts added at the foot, the read ones trimmed off the top), takes no keys, and minimises to a tab (a phone starts
 * with it minimised). It goes when the room is full.
 */
export class Doomscroll {
  open = false;
  private raf = 0; private scr: HTMLElement | null = null;
  private seen = new Set<string>();
  private min = typeof matchMedia !== 'undefined' && matchMedia('(max-width: 700px)').matches;
  constructor(private el: HTMLElement) {}

  /** Called every frame: dock the feed while you are waiting for your room to fill, and put it away when it has. */
  update(waiting: boolean) { if (waiting && !this.open) this.dock(); else if (!waiting && this.open) this.hide(); }

  private dock() {
    const root = this.el; root.textContent = ''; root.classList.add('dock'); root.classList.toggle('min', this.min);
    const bar = el('button', 'fd-bar'); bar.type = 'button'; bar.id = 'feed-toggle';
    bar.append(el('b', '', 'Feed'), el('span', '', PITCH.kick));
    bar.setAttribute('aria-expanded', String(!this.min)); bar.setAttribute('aria-controls', 'feed-screen');
    bar.onclick = () => { this.min = !this.min; root.classList.toggle('min', this.min); bar.setAttribute('aria-expanded', String(!this.min)); };
    const scr = el('div', 'fd-screen'); scr.id = 'feed-screen'; this.scr = scr;
    const top = el('div', 'fd-top'), t = new Date();
    top.append(el('span', '', `${t.getHours()}:${String(t.getMinutes()).padStart(2, '0')}`), el('span', '', 'feed'), el('span', '', '▮▮▮ 23%'));
    const pitch = el('section', 'fd-pitch');
    pitch.append(el('p', 'kick', PITCH.kick), el('h2', '', PITCH.title), el('p', 'fd-body', PITCH.body));
    scr.append(top, pitch);
    this.seen.clear(); this.more(SHOW);
    root.append(bar, scr);
    root.hidden = false; this.open = true;

    // the doomscroll: a slow, steady drift that no wheel or touch stops (the screen is overflow:hidden). Under reduced
    // motion it steps a post at a time instead of gliding
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let last = performance.now(), acc = 0, stepT = 0;
    const step = (now: number) => {
      if (!this.open) return;
      const dt = (now - last) / 1000; last = now;
      if (!this.min) {
        if (still) {
          if ((stepT += dt) >= STEP_S) { stepT = 0; const c = Array.from(scr.querySelectorAll<HTMLElement>('.fd-post')).find((x) => x.offsetTop > scr.scrollTop + 4); if (c) scr.scrollTop = c.offsetTop - 40; }
        } else if ((acc += dt * DRIFT_PX_S) >= 1) { scr.scrollTop += Math.floor(acc); acc %= 1; }
        if (scr.scrollTop + scr.clientHeight >= scr.scrollHeight - 300) this.more(MORE);
        this.trim();
      }
      this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  /** n more posts at the foot, none repeated until every one has been seen. */
  private more(n: number) {
    if (!this.scr) return;
    let fresh = pickPosts(POSTS.length).filter((p) => !this.seen.has(p.id));
    if (fresh.length < n) { this.seen.clear(); fresh = [...fresh, ...pickPosts(POSTS.length)]; }
    for (const p of fresh.slice(0, n)) { this.seen.add(p.id); this.scr.append(card(p)); }
  }

  /** Keep the page small: once there are more than KEEP posts, the read ones above the view come off the top. */
  private trim() {
    const scr = this.scr!, posts = scr.querySelectorAll<HTMLElement>('.fd-post');
    if (posts.length <= KEEP) return;
    const first = posts[0], h = first.offsetHeight + parseFloat(getComputedStyle(first).marginBottom || '0');
    if (first.offsetTop + h < scr.scrollTop) { first.remove(); scr.scrollTop -= h; }
  }

  hide() { this.open = false; cancelAnimationFrame(this.raf); this.el.hidden = true; this.el.textContent = ''; this.scr = null; }
}

/** One post as the feed prints it: avatar, name and handle, the update tag, the text, the counts. */
function card(p: Post): HTMLElement {
  const tag = TAG[p.sentiment], who = postName(p);
  const c = el('article', `fd-post k-${tag?.kind ?? 'text'}`);
  c.dataset.id = p.id;
  const head = el('div', 'fd-head'), name = el('div', 'fd-name');
  name.append(el('b', '', who), el('small', '', `${p.username} · ${p.time_posted}`));
  head.append(el('i', 'fd-av', who.trim()[0] ?? '·'), name);
  c.append(head);
  if (tag) c.append(el('p', 'fd-kind', tag.label));
  c.append(el('p', 'fd-text', p.content), el('p', 'fd-foot', `💬 ${count(p.replies)}   ↻ ${count(p.retweets)}   ♡ ${count(p.likes)}`));
  return c;
}

/** `board` and `you` come from the shared metro (docs/plans/2026-10-18-shared-metro.md): who got on the train where, and your own line */
export interface RoomEvent { k: 'join' | 'leave' | 'board'; id: number; name: string; at?: string; n: number; you?: boolean }

/** The room card while it fills: number, count, who is here, who came and went, and leaving (say the line first). */
export class LobbyWidget {
  room = 0; size = 0;
  private events: { text: string; t: number }[] = [];
  private shown = false; private asking = false; private key = '';
  constructor(private el: HTMLElement, private onLeave: () => void) {}

  reset(room: number, size: number) { this.room = room; this.size = size; this.events = []; this.asking = false; this.key = ''; }

  event(e: RoomEvent, meId: number) {
    const who = e.you || e.id === meId ? 'You' : e.name;
    const text = e.k === 'join' ? `${who} joined${e.at ? ' near ' + e.at : ''}` : e.k === 'board' ? `${who} boarded at ${e.at}` : `${who} left the kafila`;
    this.events.unshift({ text, t: performance.now() });
    this.events.length = Math.min(this.events.length, 5);
    this.key = '';
  }

  /** Called every frame; rebuilds only when something it shows has changed. */
  update(visible: boolean, phase: string, people: string[], left: number, me = -1) {
    if (!visible) { if (this.shown) { this.el.hidden = true; this.shown = false; this.asking = false; } return; }
    const now = performance.now();
    this.events = this.events.filter((e) => now - e.t < 30000);
    const dots = 1 + (Math.floor(now / 500) % 3);
    const key = [phase, people.join('|'), me, left, this.asking, dots, this.events.map((e) => e.text).join('|')].join('#');
    if (!this.shown) { this.el.hidden = false; this.shown = true; }
    if (key === this.key) return;
    this.key = key;
    const n = people.length, full = phase === 'countdown';
    const r = this.el; r.textContent = '';
    const head = el('div', 'lb-head');
    head.append(el('b', '', `ROOM ${this.room}`), el('span', full ? 'lb-n full' : 'lb-n', `${n}/${this.size}`));
    r.append(head);
    r.append(el('p', 'lb-state', full ? `Room full · the march starts in ${Math.max(1, left)}` : n >= this.size ? 'Room full' : `Searching for players${'.'.repeat(dots)}`));
    const slots = el('ul', 'lb-slots');
    for (let i = 0; i < this.size; i++) slots.append(el('li', i < n ? 'on' : '', i < n ? people[i] : 'waiting…'));
    if (me >= 0 && me < n) slots.children[me].append(el('small', 'lb-you', ' (you)'));
    r.append(slots);
    if (this.events.length) { const ev = el('ul', 'lb-ev'); ev.setAttribute('aria-live', 'polite'); for (const e of this.events) ev.append(el('li', '', e.text)); r.append(ev); }
    if (this.asking) {
      r.append(el('p', 'lb-ask', LEAVE.ask));
      const row = el('div', 'lb-row');
      const yes = el('button', 'lb-yes', LEAVE.yes), no = el('button', 'lb-no', LEAVE.no);
      yes.onclick = () => { this.asking = false; this.onLeave(); };
      no.onclick = () => { this.asking = false; this.key = ''; };
      row.append(yes, no); r.append(row);
      setTimeout(() => no.focus(), 0);
    } else {
      const b = el('button', 'lb-leave', 'Leave room'); b.id = 'lobby-leave';
      b.onclick = () => { this.asking = true; this.key = ''; };
      r.append(b);
    }
  }
}
